import React, { useMemo } from "react";
import { createColumnHelper, SortingState, Table } from "@tanstack/react-table";
import { PaneBody } from "@/components/pane/paneBody.tsx";
import { IColumn, IRow } from "@/domain/interfaces/sfTable.interface.ts";
import { applyFilters, cn } from "@/lib/utils.ts";
import { useGrid } from "@/hooks/useGrid.ts";
import { usePaneContext } from "@/components/pane/paneContext.ts";
import { HighlightedValueChange } from "@/components/shared/highlightedValueChange.tsx";
import { ESFTableType } from "@/domain/enums/dashboard.enum.ts";
import { Loader } from "@/components/shared/loader.tsx";
import { PaneSubHeader } from "@/components/pane/paneSubHeader.tsx";
import { useDashboard } from "@/lib/store";
import { IADF, useADF } from "@/hooks/useADF";
import { BINANCE_RESOLUTION_MAP } from "@/lib/datafeed/config";
import { useFilterData } from "@/hooks/useFilterData";
import { useCFGI, CFGI } from "@/hooks/useCFGI";
import { Badge } from "../ui/badge";
import { useBinanceSymbols } from "@/hooks/useBinanceSymbols";

// 0 - 19: EXTREME FEAR
// 20 - 39: FEAR
// 40 - 59: NEUTRAL
// 60 - 79: GREED
// 80 - 100: EXTREME GREED
function valueColor(value: number) {
  switch (true) {
    case value < 19:
      return "text-[#F91A36]";
    case value > 20 && value < 39:
      return "text-[#B2182C]";
    case value > 40 && value < 59:
      return "text-[#FFA028]";
    case value > 60 && value < 79:
      return "text-[#3CA64B]";
    case value > 80:
      return "text-[#52ED6A]";
    default:
      return "text-gray-500";
  }
}

const columnHelper = createColumnHelper<CFGI>();
const columns = [
  columnHelper.accessor("symbol", {
    header: "Symbol",
    enableSorting: true,
  }),
  columnHelper.accessor("cfgi", {
    header: "CFGI",
    enableSorting: true,
    cell: (info) => {
      return (
        <div className={valueColor(info.getValue())}>{info.getValue()}</div>
      );
    },
  }),
  columnHelper.accessor("data_price", {
    header: "Price",
    enableSorting: true,
    cell: (info) => {
      return (
        <div className={valueColor(info.getValue())}>{info.getValue()}</div>
      );
    },
  }),
  columnHelper.accessor("data_volatility", {
    header: "Volatility",
    enableSorting: true,
    cell: (info) => {
      return (
        <div className={valueColor(info.getValue())}>{info.getValue()}</div>
      );
    },
  }),
  columnHelper.accessor("data_volume", {
    header: "Volume",
    enableSorting: true,
    cell: (info) => {
      return (
        <div className={valueColor(info.getValue())}>{info.getValue()}</div>
      );
    },
  }),
  columnHelper.accessor("data_impulse", {
    header: "Impulse",
    enableSorting: true,
    cell: (info) => {
      return (
        <div className={valueColor(info.getValue())}>{info.getValue()}</div>
      );
    },
  }),
  columnHelper.accessor("data_technical", {
    header: "Technical",
    enableSorting: true,
    cell: (info) => {
      return (
        <div className={valueColor(info.getValue())}>{info.getValue()}</div>
      );
    },
  }),
  columnHelper.accessor("data_social", {
    header: "Social",
    enableSorting: true,
    cell: (info) => {
      return (
        <div className={valueColor(info.getValue())}>{info.getValue()}</div>
      );
    },
  }),
  columnHelper.accessor("data_dominance", {
    header: "Dominance",
    enableSorting: true,
    cell: (info) => {
      return (
        <div className={valueColor(info.getValue())}>{info.getValue()}</div>
      );
    },
  }),
  columnHelper.accessor("data_trends", {
    header: "Trends",
    enableSorting: true,
    cell: (info) => {
      return (
        <div className={valueColor(info.getValue())}>{info.getValue()}</div>
      );
    },
  }),
  columnHelper.accessor("datas_whales", {
    header: "Whales",
    enableSorting: true,
    cell: (info) => {
      return (
        <div className={valueColor(info.getValue())}>{info.getValue()}</div>
      );
    },
  }),
  columnHelper.accessor("data_orders", {
    header: "Orders",
    enableSorting: true,
    cell: (info) => {
      return (
        <div className={valueColor(info.getValue())}>{info.getValue()}</div>
      );
    },
  }),
];

const oldValues: IRow[] = [];
const highlightTimestamps: number[] = [];

export function CFGIPaneBody() {
  const nodeId = usePaneContext();
  const { getNodeConfig } = useGrid(nodeId);
  const config = getNodeConfig();

  const interval = BINANCE_RESOLUTION_MAP[config.interval] || config.interval;

  const { data: cfgi, isLoading } = useCFGI();
  const { data: binanceSymbols, isLoading: isLoadingBinanceSymbols } =
    useBinanceSymbols();

  // const filteredData = useFilterData<IADF>(cfgi?.data || []);
  // const { globalFilters, getGlobalFilterValues, blacklist } = useDashboard();
  const preparedData = useMemo(() => {
    if (!cfgi || isLoading || !binanceSymbols || isLoadingBinanceSymbols) {
      return [];
    }

    const flatSymbols = binanceSymbols.flatMap((bs) => bs.symbol);

    // filter out cfgi data if symbol not in binanceSymbols
    cfgi.map((item) => {
      if (!flatSymbols.includes(item.symbol)) {
        cfgi.splice(cfgi.indexOf(item), 1);
      }
    });

    cfgi.map((item) => {
      item.symbol = item.symbol.includes("USDT")
        ? item.symbol
        : item.symbol.toUpperCase() + "USDT";
    });

    // let preparedData = applyFilters(
    //   filteredData,
    //   globalFilters,
    //   getGlobalFilterValues(),
    // );

    // if (blacklist.length > 0) {
    //   blacklist.forEach((symbol) => {
    //     preparedData = preparedData.filter((item) => item.symbol !== symbol);
    //   });
    // }

    // if (config?.filters && config?.filterValues) {
    //   return applyFilters<IADF>(
    //     preparedData,
    //     config.filters,
    //     config.filterValues,
    //   );
    // }

    return cfgi;
  }, [binanceSymbols, cfgi, isLoading, isLoadingBinanceSymbols]);

  return isLoading ? (
    <Loader />
  ) : (
    <>
      <PaneSubHeader />
      <div className="flex items-center justify-center gap-2">
        <Badge className="text-xxs bg-[#F91A36]">0 - 19: EXTREME FEAR</Badge>
        <Badge className="text-xxs bg-[#B2182C]">20 - 39: FEAR</Badge>
        <Badge className="text-xxs bg-[#FFA028]">40 - 59: NEUTRAL</Badge>
        <Badge className="text-xxs bg-[#3CA64B]">60 - 79: GREED</Badge>
        <Badge className="text-xxs bg-[#52ED6A]">80 - 100: EXTREME GREED</Badge>
      </div>
      <PaneBody
        className="!h-[calc(100%-24px)]"
        tableColumns={columns as IColumn[]}
        data={preparedData}
      />
    </>
  );
}
